import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { saveAs } from 'file-saver';
import TodoRegisterLayer from '../../../../components/todo/TodoRegisterLayer';

const AVATAR_COLORS = [
  '#2196F3', '#9C27B0', '#FF9800', '#4CAF50', '#00BCD4',
  '#3F51B5', '#009688', '#FFC107', '#795548', '#607D8B'
];

const getAvatarColorInfo = (identifier) => {
  if (!identifier) return { bg: '#0d6efd', text: 'light' };
  const str = String(identifier);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(Math.imul(hash, 2654435761));
  const index = hash % AVATAR_COLORS.length;
  const color = AVATAR_COLORS[index];
  const isDarkText = ['#FFC107', '#FF9800', '#00BCD4', '#4CAF50'].includes(color);
  return { bg: color, text: isDarkText ? 'dark' : 'light' };
};

function CommentsTab({ comments: realComments, newComment, setNewComment, selectedFiles = [], setSelectedFiles, fileInputRef, handleAddComment, handleDeleteComment, handleEditCommentSubmit, openFolderModal, replyTargetId, setReplyTargetId, replyContent, setReplyContent, handleReplySubmit, isFullScreenComments, currentUser, folders, isPureWatcher, isProjectMaster, project, task, focusId }) {
  const location = useLocation();

  // 가상 동작(수정, 저장, 삭제)이 화면 상에서 직접 메모리 상태로 작동하도록 로컬 상태 도입
  const comments = realComments;
  const [editTextCommentId, setEditTextCommentId] = React.useState(null);
  const [editTextCommentContent, setEditTextCommentContent] = React.useState('');

  const [replyFiles, setReplyFiles] = React.useState([]);
  const replyFileInputRef = React.useRef(null);
  const [editingCommentId, setEditingCommentId] = React.useState(null);
  const [editContent, setEditContent] = React.useState('');
  const [editFiles, setEditFiles] = React.useState([]);
  const editFileInputRef = React.useRef(null);
  const [addingFileCommentId, setAddingFileCommentId] = React.useState(null);
  const [additionalFiles, setAdditionalFiles] = React.useState([]);
  const additionalFileInputRef = React.useRef(null);

  const [activeFilePopup, setActiveFilePopup] = React.useState(null);
  const [textReplyTargetId, setTextReplyTargetId] = React.useState(null);

  const renderTextReplies = (rootParentId) => {
    // 1. 재귀적으로 루트 코멘트에 달린 모든 텍스트 답글(대댓글 등) 평면화 수집
    const getAllDescendants = (parentId) => {
      let result = [];
      const children = comments.filter(c => c.parent_id === parentId && !checkIsFile(c));
      for (const child of children) {
        result.push(child);
        result = result.concat(getAllDescendants(child.id));
      }
      return result;
    };
    
    let allReplies = getAllDescendants(rootParentId);
    if (allReplies.length === 0) return null;
    
    // 2. 시간순으로 정렬
    allReplies.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    return (
      <div className="d-flex flex-column" style={{ paddingLeft: '8px' }}>
        {allReplies.map((reply, idx) => {
          const replyAuthorId = reply.user_id || reply.user?.id || reply.user?.userid;
          const replyName = reply.user?.name || '알 수 없음';
          const replyPos = reply.user?.org?.position || reply.user?.position ? ` ${reply.user?.org?.position || reply.user?.position}` : '';
          const replyDept = reply.user?.org?.dept_name || reply.user?.dept_name ? ` [${reply.user?.org?.dept_name || reply.user?.dept_name}]` : '';
          const replyFullName = `${replyName}${replyPos}${replyDept}`;

          const avatarInfo = getAvatarColorInfo(replyAuthorId || replyName);
          const photoPath = reply.user?.org?.photo_path || reply.user?.photo_path;

          return (
            <div key={`text-reply-${reply.id}`} id={`comment-${reply.id}`} data-feed-id={`feed-${reply.id}`} className={`py-1 position-relative d-flex ${idx > 0 ? 'mt-1' : ''} action-container animation-fade-in`}>
              {/* 공통 세로선 및 L자 꺾쇠 연결선 (1단 Flat) */}
              <div className="reply-connector" style={{ left: '-12px', width: '12px' }}></div>

              <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm fw-bold fs-9 overflow-hidden flex-shrink-0"
                style={{ width: '22px', height: '22px', backgroundColor: photoPath ? 'transparent' : avatarInfo.bg, color: avatarInfo.text === 'dark' ? '#333' : '#fff', marginTop: '2px', zIndex: 1 }}>
                {photoPath ? (
                  <img src={photoPath} alt={replyName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  replyName !== '알 수 없음' ? replyName.charAt(0) : '?'
                )}
              </div>

              <div className="ms-2 flex-grow-1 min-w-0">
                <div className="d-flex align-items-center mb-1 flex-wrap">
                  <span className="fw-bold text-dark fs-8 me-2">{replyFullName}</span>
                  <span className="text-muted" style={{ fontSize: '11px' }}>{formatDateTime(reply.created_at)}</span>
                  
                  <div className="d-flex gap-1 align-items-center opacity-0 action-buttons transition-opacity ms-2 flex-shrink-0">
                    {!isPureWatcher && (
                      <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title={`${replyName}님에게 답글 달기`} onClick={() => {
                        setTextReplyTargetId(reply.id);
                        setReplyContent('');
                        setEditTextCommentId(null);
                      }}>
                        <i className="fas fa-reply fs-9"></i>
                      </button>
                    )}
                    {!isPureWatcher && (
                      <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="수정" onClick={() => {
                        setEditTextCommentId(reply.id);
                        setEditTextCommentContent(reply.content);
                        setTextReplyTargetId(null);
                      }}>
                        <i className="fas fa-edit fs-9"></i>
                      </button>
                    )}
                    {!isPureWatcher && (
                      <button className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="삭제" onClick={() => handleDeleteComment && handleDeleteComment(reply.id)}>
                        <i className="fas fa-trash-alt fs-9"></i>
                      </button>
                    )}
                  </div>
                </div>

                {editTextCommentId === reply.id ? (
                  <div className="d-flex flex-column gap-2 mt-1">
                    <textarea
                      className="form-control form-control-sm shadow-none"
                      rows="2"
                      value={editTextCommentContent}
                      onChange={(e) => setEditTextCommentContent(e.target.value)}
                      style={{ resize: 'none', fontSize: '12px', border: '1px solid #ced4da' }}
                    ></textarea>
                    <div className="d-flex justify-content-end gap-2">
                      <button className="btn btn-sm text-white rounded-1 px-2 fw-bold shadow-sm" style={{ backgroundColor: '#4c6ef5', fontSize: '10px', height: '22px', lineHeight: '1' }} onClick={async () => {
                        if (handleEditCommentSubmit) {
                          const success = await handleEditCommentSubmit(reply.id, editTextCommentContent);
                          if (success) setEditTextCommentId(null);
                        }
                      }}>저장</button>
                      <button className="btn btn-sm text-white rounded-1 px-2 fw-bold shadow-sm" style={{ backgroundColor: '#868e96', fontSize: '10px', height: '22px', lineHeight: '1' }} onClick={() => setEditTextCommentId(null)}>취소</button>
                    </div>
                  </div>
                ) : (
                  <div className="text-dark custom-scrollbar w-100" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px', lineHeight: '1.4' }}>
                    {reply.target_mention && <span className="text-primary fw-bold me-1">@{reply.target_mention}</span>}
                    {reply.content}
                  </div>
                )}

                {textReplyTargetId === reply.id && (
                  <div className="mt-2 pt-2" style={{ borderTop: '1px dashed rgba(0,0,0,0.08)' }}>
                    <div className="d-flex gap-2 align-items-stretch">
                      <textarea
                        className="form-control form-control-sm shadow-none"
                        rows="2"
                        placeholder={`@${replyName}님에게 텍스트 답글...`}
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        style={{ resize: 'none', fontSize: '12px', border: '1px solid #ced4da', flexGrow: 1 }}
                        autoFocus
                      ></textarea>
                      <div className="d-flex flex-column gap-1 justify-content-center flex-shrink-0">
                        <button className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-grow-1" style={{ backgroundColor: '#4c6ef5', fontSize: '10px', lineHeight: '1' }} onClick={() => {
                          if (handleReplySubmit) {
                            handleReplySubmit(rootParentId, replyContent, [], replyName || null);
                          }
                          setTextReplyTargetId(null);
                          setReplyContent('');
                        }}>저장</button>
                        <button className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-grow-1" style={{ backgroundColor: '#868e96', fontSize: '10px', lineHeight: '1' }} onClick={() => { setTextReplyTargetId(null); setReplyContent(''); }}>취소</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  const popupTimeoutRef = React.useRef(null);

  const handleFileLayerEnter = (e, source) => {
    if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);

    let files;
    if (source === 'selected') files = selectedFiles;
    else if (source === 'edit') files = editFiles;
    else if (source === 'additional') files = additionalFiles;
    else if (source === 'reply') files = replyFiles;

    if (!files || files.length <= 1) return;

    // 동적 위치 계산
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const layerHeight = 260;

    setActiveFilePopup({
      source,
      rect,
      isTop: spaceBelow < layerHeight && rect.top > layerHeight
    });
  };

  const handleFileLayerLeave = () => {
    popupTimeoutRef.current = setTimeout(() => {
      setActiveFilePopup(null);
    }, 150);
  };

  const renderPortalPopup = () => {
    if (!activeFilePopup) return null;
    const { source, rect, isTop } = activeFilePopup;

    let files, filesSetter;
    if (source === 'selected') { files = selectedFiles; filesSetter = setSelectedFiles; }
    else if (source === 'edit') { files = editFiles; filesSetter = setEditFiles; }
    else if (source === 'additional') { files = additionalFiles; filesSetter = setAdditionalFiles; }
    else if (source === 'reply') { files = replyFiles; filesSetter = setReplyFiles; }

    if (!files || files.length <= 1) return null;

    const maxLeft = window.innerWidth - 320;
    const safeLeft = Math.min(rect.left, maxLeft);

    const popupStyle = {
      position: 'fixed',
      left: safeLeft,
      minWidth: '300px',
      zIndex: 2147483647,
      ...(isTop
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 })
    };

    const popupContent = (
      <div
        className={`premium-file-layer ${isTop ? 'popup-top' : ''}`}
        style={{ ...popupStyle, visibility: 'visible', opacity: 1, transform: 'translateY(0) scale(1)', pointerEvents: 'auto' }}
        onMouseEnter={() => { if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current); }}
        onMouseLeave={handleFileLayerLeave}
      >
        <div className="premium-file-layer-header fs-8 fw-bold text-muted d-flex justify-content-between align-items-center">
          <span>첨부된 파일 ({files.length})</span>
          <span style={{ cursor: 'pointer', fontSize: '11px', transition: 'opacity 0.2s' }}
            className="text-danger opacity-75"
            onMouseEnter={(e) => e.target.classList.replace('opacity-75', 'opacity-100')}
            onMouseLeave={(e) => e.target.classList.replace('opacity-100', 'opacity-75')}
            onClick={(e) => { e.stopPropagation(); filesSetter([]); setActiveFilePopup(null); }}>전체 삭제</span>
        </div>
        <div className="premium-file-layer-body" style={{ maxHeight: '200px', overflowY: 'auto', padding: '0 12px 8px 8px' }}>
          {files.map((f, idx) => (
            <div key={idx} className="d-flex justify-content-between align-items-center premium-file-item">
              <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                <div className="premium-file-icon me-2 flex-shrink-0">
                  <i className="fas fa-file-alt"></i>
                </div>
                <span className="text-truncate fw-bold text-dark flex-grow-1 premium-file-name" title={f.name}>{f.name}</span>
              </div>
              <div className="premium-delete-btn flex-shrink-0 ms-1" style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  const newFiles = files.filter((_, i) => i !== idx);
                  filesSetter(newFiles);
                  if (newFiles.length <= 1) setActiveFilePopup(null);
                }} title="삭제">
                <i className="fas fa-times" style={{ fontSize: '11px' }}></i>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
    return createPortal(popupContent, document.body);
  };



  // --- 통합 포커스 핸들러: 외부 알림 / To-Do 등에서 접근 시 해당 항목(루트 및 대댓글)으로 자동 스크롤 & 3초 펄스 강조 ---
  const focusHandledRef = React.useRef(null);

  useEffect(() => {
    const targetFocusId = focusId || new URLSearchParams(location.search).get('focusId');
    if (!targetFocusId || !comments || comments.length === 0) return;
    if (focusHandledRef.current === targetFocusId) return;

    let attempts = 0;
    const tryScroll = setInterval(() => {
      attempts++;
      let targetEl = document.getElementById(`comment-${targetFocusId}`) || document.getElementById(`feed-${targetFocusId}`);
      if (!targetEl) {
        targetEl = document.querySelector(`.comment-group-container[data-comment-ids*="${targetFocusId}"]`);
      }
      if (targetEl) {
        clearInterval(tryScroll);
        focusHandledRef.current = targetFocusId;
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        targetEl.classList.add('highlight-comment');
        setTimeout(() => {
          targetEl.classList.remove('highlight-comment');
        }, 3000);

        // URL에 남아있는 focusId 파라미터 안전하게 정리 (새로고침/재진입 시 중복 방지)
        const sp = new URLSearchParams(window.location.search);
        if (sp.has('focusId')) {
          sp.delete('focusId');
          const cleanUrl = window.location.pathname + (sp.toString() ? '?' + sp.toString() : '');
          window.history.replaceState(null, '', cleanUrl);
        }
      } else if (attempts > 20) {
        clearInterval(tryScroll);
      }
    }, 150);

    return () => clearInterval(tryScroll);
  }, [focusId, comments, location.search]);

  // To-Do 등록 팝업 상태
  const [todoAnchorEl, setTodoAnchorEl] = React.useState(null);
  const [todoSource, setTodoSource] = React.useState(null);

  const handleOpenTodoRegister = (e, item) => {
    e.stopPropagation();
    setTodoAnchorEl({ current: e.currentTarget });

    let contentStr = '';
    if (item.items && item.items.length > 0) {
      const textItem = item.items.find(i => i.content && !checkIsFile(i));
      contentStr = textItem ? textItem.content : '';
    } else if (item.content && !checkIsFile(item)) {
      contentStr = item.content;
    }

    setTodoSource({
      ref_type: 'pms_comment',
      ref_table: 'tdx_pms_task_comment',
      ref_id: item.id,
      add_title: project?.title || '',
      add_subtype: 'task',
      add_id1: project?.id,
      add_id2: task?.id,
      title: task?.task_name || task?.title || '태스크',
      memo: contentStr, // 등록된 코멘트 내용
    });
  };

  const getFileIconClass = (fileName) => {
    if (!fileName) return 'fas fa-file-alt text-secondary';
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'fas fa-file-pdf text-danger';
      case 'doc': case 'docx': case 'rtf': return 'fas fa-file-word text-primary';
      case 'xls': case 'xlsx': case 'csv': return 'fas fa-file-excel text-success';
      case 'ppt': case 'pptx': case 'pps': return 'fas fa-file-powerpoint text-warning';
      case 'hwp': case 'hwpx': return 'fas fa-file-alt text-info';
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'svg': case 'webp': case 'bmp': case 'tiff': case 'ico': return 'fas fa-file-image text-info';
      case 'zip': case 'rar': case '7z': case 'tar': case 'gz': case 'alz': case 'egg': return 'fas fa-file-archive text-secondary';
      case 'mp4': case 'avi': case 'mkv': case 'mov': case 'wmv': case 'flv': case 'webm': return 'fas fa-file-video text-danger';
      case 'mp3': case 'wav': case 'ogg': case 'flac': case 'm4a': return 'fas fa-file-audio text-warning';
      case 'js': case 'json': case 'html': case 'css': case 'xml': case 'py': case 'java': case 'c': case 'cpp': case 'sql': case 'log': case 'sh': case 'php': return 'fas fa-file-code text-dark';
      case 'txt': case 'md': return 'fas fa-file-alt text-secondary';
      default: return 'fas fa-file text-muted';
    }
  };

  const checkIsFile = (c) => {
    if (!c) return false;
    if (c.file || c.file_path || c.file_name) return true;
    return c.content && /\.(gif|jpg|jpeg|png|pdf|zip|doc|docx|xls|xlsx|ppt|pptx|txt|hwp|csv|ai|psd)$/i.test(c.content.trim());
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return '방금 전';
    if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}일 전`;
    const pad = n => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const stripUniqueKey = (name) => {
    if (!name) return name;
    return name.replace(/_[0-9]{8}F[0-9]{6}(?=\.[^.]+$|$)/g, '');
  };

  const handleDownloadFile = async (fileObj) => {
    if (!fileObj) {
      alert('다운로드할 파일 정보가 없습니다.');
      return;
    }
    const downloadPath = fileObj.file_path || fileObj.path || (fileObj.file_name ? `/uploads/techni/pms/${fileObj.file_name}` : null) || (fileObj.originalName ? `/uploads/techni/pms/${fileObj.originalName}` : null);
    if (!downloadPath) {
      alert('다운로드할 파일 정보가 없습니다.');
      return;
    }
    try {
      const response = await fetch(downloadPath);
      const blob = await response.blob();
      saveAs(blob, stripUniqueKey(fileObj.file_name || 'download'));
    } catch (e) {
      console.error('다운로드 실패:', e);
      alert('파일 다운로드 중 오류가 발생했습니다.');
    }
  };

  const renderReplies = (parentId, rootAuthorId = null, depth = 0) => {
    const childReplies = comments.filter(c => c.parent_id === parentId);
    if (childReplies.length === 0) return null;

    const sortedReplies = [...childReplies].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return sortedReplies.map(reply => {
      const isReplyFile = checkIsFile(reply);
      const replyAuthorId = reply.user_id || reply.user?.id || reply.user?.userid;
      const isSameAuthor = rootAuthorId && String(replyAuthorId) === String(rootAuthorId);
      const replyName = reply.user?.name || '알 수 없음';
      const replyPos = reply.user?.org?.position || reply.user?.position ? ` ${reply.user?.org?.position || reply.user?.position}` : '';
      const replyDept = reply.user?.org?.dept_name || reply.user?.dept_name ? ` [${reply.user?.org?.dept_name || reply.user?.dept_name}]` : '';
      const replyFullName = `${replyName}${replyPos}${replyDept}`;

      return (
        <div key={reply.id} id={`comment-${reply.id}`} className="mt-1 mb-1 animation-fade-in" style={{ marginLeft: `${depth * 15}px` }}>
          {isReplyFile ? (
            <div className="d-flex align-items-center action-container">
              <i className="fas fa-level-up-alt fa-rotate-90 text-secondary me-2 fs-6 flex-shrink-0"></i>
              <i className={`${getFileIconClass(stripUniqueKey(reply.file?.originalName || reply.content))} fa-fw me-2 flex-shrink-0`} style={{ fontSize: '18px', verticalAlign: 'middle' }}></i>
              <span
                className="text-dark fw-bold fs-7 me-2 text-truncate"
                style={{ cursor: 'pointer', maxWidth: `calc(min(350px, 20vw) - ${depth * 15}px)` }}
                onClick={() => handleDownloadFile(reply.file)}
                onMouseEnter={(e) => {
                  if (e.target.offsetWidth < e.target.scrollWidth) {
                    e.target.title = stripUniqueKey(reply.content);
                  } else {
                    e.target.title = '';
                  }
                }}
              >
                {stripUniqueKey(reply.content)}
              </span>
              <span className="text-muted fs-8 me-3 flex-shrink-0">[{Math.round((reply.file?.file_size || 1024) / 1024)}kb]</span>

              <span className="text-secondary fs-7 flex-shrink-0">
                {isSameAuthor ? formatDateTime(reply.created_at) : (
                  <>{replyFullName} <span className="text-muted mx-1">|</span> {formatDateTime(reply.created_at)}</>
                )}
              </span>

              <div className="d-flex gap-1 align-items-center opacity-0 action-buttons transition-opacity ms-2 flex-shrink-0">
                <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="다운로드" onClick={() => handleDownloadFile(reply.file)}>
                  <i className="fas fa-download"></i>
                </button>
                {!isPureWatcher && (
                  <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="답글(파일첨부)" onClick={() => setReplyTargetId(reply.id)}>
                    <i className="fas fa-plus"></i>
                  </button>
                )}
                {!isPureWatcher && (isProjectMaster || (currentUser && currentUser.name === reply.user?.name)) && (
                  <button className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="삭제" onClick={() => handleDeleteComment(reply.id)}>
                    <i className="fas fa-trash-alt"></i>
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="d-flex align-items-center">
                <i className="fas fa-level-up-alt fa-rotate-90 text-secondary me-2 fs-6 flex-shrink-0"></i>
                <span className="text-secondary fs-7 flex-shrink-0">
                  {isSameAuthor ? formatDateTime(reply.created_at) : (
                    <>{replyFullName} <span className="text-muted mx-1">|</span> {formatDateTime(reply.created_at)}</>
                  )}
                </span>
              </div>
              <div className="action-container position-relative w-100 mt-1">
                <div className="text-dark fs-7 custom-scrollbar w-100" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '84px', overflowY: 'auto' }}>{reply.content}</div>
                <div className="position-absolute d-flex gap-1 align-items-center opacity-0 action-buttons transition-opacity" style={{ right: '-5px', top: '0px' }}>
                  <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="To-Do 등록" onClick={(e) => handleOpenTodoRegister(e, reply)}>
                    <i className="fas fa-tasks"></i>
                  </button>
                  {!isPureWatcher && (
                    <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="답글(파일첨부)" onClick={() => setReplyTargetId(reply.id)}>
                      <i className="fas fa-plus"></i>
                    </button>
                  )}
                  {!isPureWatcher && (isProjectMaster || (currentUser && currentUser.name === reply.user?.name)) && (
                    <button className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="삭제" onClick={() => handleDeleteComment(reply.id)}>
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  )}
                </div>
              </div>
            </>
          )}


          {replyTargetId === reply.id && (
            <div className="mt-2 mb-2 d-flex align-items-center gap-2 animation-fade-in">
              <div className="d-flex bg-white border flex-grow-1 align-items-center" style={{ height: '28px' }}>
                <input type="text" className="form-control border-0 shadow-none h-100 flex-grow-1 rounded-0 fs-8 px-2 bg-white"
                  placeholder={replyFiles.length > 0 ? `${replyFiles[0].name}${replyFiles.length > 1 ? ` 외 ${replyFiles.length - 1}개` : ''}` : "첨부할 파일을 선택하세요..."}
                  readOnly
                  onClick={() => replyFileInputRef.current?.click()}
                  style={{ cursor: 'pointer' }}
                />
                <input type="file" className="d-none" ref={replyFileInputRef} multiple onChange={(e) => setReplyFiles(Array.from(e.target.files))} />
                <button className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center" title="파일 첨부" onClick={() => replyFileInputRef.current?.click()}>
                  <i className="fas fa-upload fs-8"></i>
                </button>
              </div>
              <button className="btn btn-sm text-white rounded-1 px-2 fw-bold fs-8 shadow-sm" style={{ backgroundColor: '#4c6ef5', height: '28px' }}
                onClick={() => { handleReplySubmit(reply.id, replyContent, replyFiles); setReplyFiles([]); }}>저장</button>
              <button className="btn btn-sm text-white rounded-1 px-2 fw-bold fs-8 shadow-sm" style={{ backgroundColor: '#868e96', height: '28px' }}
                onClick={() => { setReplyTargetId(null); setReplyFiles([]); }}>취소</button>
            </div>
          )}

          {renderReplies(reply.id, rootAuthorId, depth + 1)}
        </div>
      );
    });
  };

  const groupComments = (commentList) => {
    if (!commentList || commentList.length === 0) return [];
    
    // 핵심 버그 수정: 답글(parent_id가 존재하는 코멘트)은 최상위 그룹핑 대상에서 무조건 제외
    const rootCommentsOnly = commentList.filter(c => !c.parent_id || c.parent_id === 0);
    if (rootCommentsOnly.length === 0) return [];

    const sorted = [...rootCommentsOnly].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    const grouped = [];
    let currentGroup = { ...sorted[0], items: [sorted[0]] };

    for (let i = 1; i < sorted.length; i++) {
      const c = sorted[i];
      if (c.user_id === currentGroup.user_id && c.created_at === currentGroup.created_at) {
        currentGroup.items.push(c);
      } else {
        grouped.push(currentGroup);
        currentGroup = { ...c, items: [c] };
      }
    }
    grouped.push(currentGroup);
    return grouped;
  };

  return (
    <div className="animation-fade-in d-flex flex-column h-100">
      <style>{`
        .hover-bg-gray:hover { background-color: #e9ecef !important; }
        .action-container:hover .action-buttons { opacity: 1 !important; }
        .transition-opacity { transition: opacity 0.2s ease; }
        .avatar-wrapper { position: relative; display: inline-block; }
        .file-container { position: relative; }

        /* Bulletproof z-index fix */
        .comment-group-container:hover { z-index: 1020 !important; }
        .new-comment-container:hover { z-index: 1020 !important; }

        /* Text Reply Connector - 연하고 미려한 꺾쇠선 */
        .reply-connector {
          position: absolute;
          top: -2px;
          height: 18px;
          border-left: 2px solid rgba(173, 181, 189, 0.4);
          border-bottom: 2px solid rgba(173, 181, 189, 0.4);
          border-bottom-left-radius: 6px;
        }
        
        /* Premium File Layer Styling */
        .premium-file-layer {
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 12px 36px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(0, 0, 0, 0.06);
          z-index: 1050;
          
          /* Smooth Animation */
          visibility: hidden;
          opacity: 0;
          transform: translateY(10px) scale(0.98);
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          transform-origin: top center;
        }

        /* 꼬다리 (Arrow) */
        .premium-file-layer::before {
          content: '';
          position: absolute;
          top: -6px;
          left: 30px;
          width: 12px;
          height: 12px;
          background: #f8f9fa;
          border-left: 1px solid rgba(0,0,0,0.06);
          border-top: 1px solid rgba(0,0,0,0.06);
          transform: rotate(45deg);
          border-radius: 2px 0 0 0;
          z-index: 1;
        }

        .file-container:hover .premium-file-layer {
          visibility: visible;
          opacity: 1;
          transform: translateY(0) scale(1);
        }

        /* 팝업 위로 뜨기 (Dynamic Positioning) */
        .premium-file-layer.popup-top {
          top: auto !important;
          bottom: calc(100% + 4px);
          transform-origin: bottom center;
        }
        .file-container:hover .premium-file-layer.popup-top {
          transform: translateY(0) scale(1);
        }
        .premium-file-layer.popup-top::before {
          top: auto;
          bottom: -6px;
          background: #ffffff; /* 바닥 색상에 맞춤 */
          border-left: none;
          border-top: none;
          border-right: 1px solid rgba(0,0,0,0.06);
          border-bottom: 1px solid rgba(0,0,0,0.06);
          border-radius: 0 0 2px 0;
        }
        
        /* Premium Header */
        .premium-file-layer-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          padding: 8px 12px;
          border-radius: 12px 12px 0 0;
          margin-bottom: 6px;
          position: relative;
          z-index: 2;
        }

        /* Premium Scrollbar */
        .premium-file-layer-body::-webkit-scrollbar { width: 6px; }
        .premium-file-layer-body::-webkit-scrollbar-track { background: transparent; }
        .premium-file-layer-body::-webkit-scrollbar-thumb { background: rgba(0, 0, 0, 0.15); border-radius: 10px; }
        .premium-file-layer-body::-webkit-scrollbar-thumb:hover { background: rgba(0, 0, 0, 0.25); }
        
        /* Premium Items */
        .premium-file-item {
          border-radius: 6px;
          transition: all 0.2s ease;
          padding: 4px 8px;
          margin-bottom: 1px;
        }
        .premium-file-item:hover { background-color: #f1f3f5; }
        
        /* Premium Delete Button */
        .premium-delete-btn {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #adb5bd;
          transition: all 0.2s ease;
          background: transparent;
        }
        .premium-delete-btn:hover { background: #ffe3e3; color: #fa5252; }
        
        /* Premium File Icon */
        .premium-file-icon {
          color: #748ffc;
          background: #edf2ff;
          width: 22px;
          height: 22px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.65rem;
        }

        /* Font size */
        .premium-file-name {
          font-size: 11px;
        }

        /* 알림 클릭 시 대상 코멘트 3초 펄스 강조 스타일 */
        @keyframes comment-pulse {
          0% { box-shadow: 0 0 0 0px rgba(13, 110, 253, 0); background-color: rgba(13, 110, 253, 0); }
          50% { box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.25); background-color: rgba(13, 110, 253, 0.08); }
          100% { box-shadow: 0 0 0 0px rgba(13, 110, 253, 0); background-color: rgba(13, 110, 253, 0); }
        }
        .highlight-comment {
          animation: comment-pulse 1s ease-in-out 3;
          border-radius: 8px;
          transition: all 0.3s ease;
        }
      `}</style>
      {!isFullScreenComments && !isPureWatcher && (
        <div className="bg-white p-3 rounded shadow-sm mb-4 border border-light new-comment-container position-relative" style={{ zIndex: 1001 }}>
          <style>{`
            .light-placeholder::placeholder {
              color: #adb5bd !important;
              opacity: 0.6 !important;
            }
          `}</style>
          <textarea
            className="form-control border-0 shadow-none bg-light mb-2 light-placeholder"
            placeholder="작업 진행 상황이나 코멘트를 남겨보세요."
            rows="3"
            style={{ resize: 'none', fontSize: '0.8rem' }}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          ></textarea>

          <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
            <input type="file" className="d-none" ref={fileInputRef} multiple onChange={(e) => setSelectedFiles(Array.from(e.target.files))} />

            <div className="d-flex align-items-center flex-grow-1 min-w-0 me-3">
              <button
                className="btn btn-light text-secondary d-inline-flex align-items-center px-3 border-0 rounded-pill flex-shrink-0"
                style={{ fontSize: '0.9rem', height: '36px' }}
                onClick={() => fileInputRef.current?.click()}
                title="파일 첨부"
              >
                <i className="fas fa-paperclip me-2"></i>첨부
              </button>

              <div
                className={`file-container d-flex align-items-center rounded-pill px-3 ms-2 flex-grow-1 min-w-0 ${selectedFiles.length > 0 ? 'bg-white' : ''}`}
                style={{
                  height: '36px',
                  border: selectedFiles.length > 0 ? '1px solid #dee2e6' : '1px dashed #ced4da',
                  backgroundColor: selectedFiles.length > 0 ? '#ffffff' : '#f8f9fa'
                }}
              >
                <span className={`text-truncate ${selectedFiles.length > 0 ? 'text-secondary' : 'text-muted'}`} style={{ fontSize: '0.85rem', cursor: selectedFiles.length > 1 ? 'pointer' : 'default' }} onMouseEnter={(e) => handleFileLayerEnter(e, 'selected')} onMouseLeave={handleFileLayerLeave}>
                  {selectedFiles.length > 0
                    ? `${selectedFiles[0].name}${selectedFiles.length > 1 ? ` 외 ${selectedFiles.length - 1}개` : ''}`
                    : '첨부된 파일이 없습니다.'}
                </span>
                {selectedFiles.length > 0 && (
                  <i className="fas fa-times text-muted ms-auto flex-shrink-0" style={{ cursor: 'pointer' }} onClick={() => setSelectedFiles([])} title="첨부 전체 취소"></i>
                )}
              </div>
            </div>

            <button className="btn btn-primary fw-bold px-4 rounded-pill shadow-sm flex-shrink-0" onClick={handleAddComment}>
              등록
            </button>
          </div>
        </div>
      )}

      <div className="flex-grow-1 overflow-auto bg-white py-2 px-3 rounded-3 shadow-sm border" style={{ borderColor: '#dee2e6' }}>
        {comments.length === 0 ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5 mt-4">
            <div className="rounded-circle d-flex align-items-center justify-content-center mb-3 shadow-sm" style={{ width: '80px', height: '80px', backgroundColor: '#eef2ff' }}>
              <i className="far fa-comments text-primary" style={{ fontSize: '2.2rem' }}></i>
            </div>
            <h6 className="fw-bold text-dark mb-2">등록된 코멘트나 파일이 없습니다</h6>
            <p className="text-muted fs-7 mb-0 text-center">작업 진행 상황이나 피드백을 자유롭게 남기고<br />팀원들과 원활하게 소통해 보세요!</p>
          </div>
        ) : (
          <div className="position-relative ms-3 mt-3 pb-1" style={{ paddingLeft: '16px' }}>
            {groupComments(comments.filter(c => !c.parent_id)).map((group, index, arr) => {
              const isLast = index === arr.length - 1;

              const authorName = group.user?.name || 'Unknown';
              const authorPosition = group.user?.org?.position ? ` ${group.user.org.position}` : (group.user?.position ? ` ${group.user.position}` : '');
              const authorDept = group.user?.org?.dept_name ? ` [${group.user.org.dept_name}]` : (group.user?.dept_name ? ` [${group.user.dept_name}]` : '');
              const authorFullName = `${authorName}${authorPosition}${authorDept}`;
              const avatarInfo = getAvatarColorInfo(group.user?.id || group.user?.userid || authorName);
              const photoPath = group.user?.org?.photo_path || group.user?.photo_path;

              return (
                <div id={`comment-${group.id}`} key={group.id} data-comment-ids={group.items ? group.items.map(i => i.id).join(',') : String(group.id)} className="mb-2 position-relative comment-group-container" style={{ paddingLeft: '28px', zIndex: 1000 - index }}>
                  {!isLast && (
                    <div className="position-absolute bg-secondary bg-opacity-25" style={{ width: '2px', top: '36px', bottom: '-14px', left: '0px', zIndex: 0 }}></div>
                  )}

                  <div className="position-absolute avatar-wrapper" style={{ left: '-15px', top: '4px', zIndex: 10 }}>
                    <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm fw-bold fs-7 overflow-hidden"
                      style={{ width: '32px', height: '32px', backgroundColor: photoPath ? 'transparent' : avatarInfo.bg, color: avatarInfo.text === 'dark' ? '#333' : '#fff' }}>
                      {photoPath ? (
                        <img src={photoPath} alt={authorName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        authorName !== 'Unknown' ? authorName.charAt(0) : '?'
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-4 shadow-sm position-relative w-100 border border-secondary border-opacity-10" style={{ borderTopLeftRadius: '4px' }}>
                    <div className="action-container">
                      <div className="ps-3 pe-2 py-1 border-bottom border-secondary border-opacity-10 bg-light d-flex align-items-center" style={{ borderTopRightRadius: '0.5rem', borderTopLeftRadius: '4px' }}>
                        <span className="fw-bold text-dark fs-7 me-2">{authorFullName}</span>
                        <span className="text-muted fs-8">{formatDateTime(group.created_at)}</span>

                        <div className="d-flex ms-auto flex-shrink-0 gap-1 opacity-0 action-buttons transition-opacity">
                          {group.items.some(i => !checkIsFile(i) && i.content) && (
                            <div style={{ position: 'relative' }}>
                              <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="To-Do 등록" onClick={(e) => handleOpenTodoRegister(e, group)}>
                                <i className="fas fa-tasks"></i>
                              </button>
                              {todoSource && todoSource.ref_id === group.id && (
                                <TodoRegisterLayer
                                  source={todoSource}
                                  anchorRef={todoAnchorEl}
                                  onClose={() => setTodoSource(null)}
                                  direction="down"
                                  align="right"
                                />
                              )}
                            </div>
                          )}
                          {!isPureWatcher && (
                            <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="추가 첨부" onClick={() => {
                              setAddingFileCommentId(group.id);
                              setAdditionalFiles([]);
                            }}>
                              <i className="fas fa-plus"></i>
                            </button>
                          )}
                          {!isPureWatcher && group.items.some(i => !checkIsFile(i) && i.content) && (
                            <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title={`${group.user?.name || '작성자'}님에게 답글 달기`} onClick={() => {
                              const textItem = group.items.find(i => !checkIsFile(i));
                              if (textItem) {
                                setTextReplyTargetId(textItem.id);
                                setReplyContent('');
                              }
                            }}>
                              <i className="fas fa-reply"></i>
                            </button>
                          )}
                          {!isPureWatcher && (isProjectMaster || (currentUser && currentUser.name === group.user?.name)) && editingCommentId !== group.id && (
                            <>
                              <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="수정" onClick={() => {
                                const textItem = group.items.find(i => !checkIsFile(i));
                                setEditingCommentId(group.id);
                                setEditContent(textItem ? textItem.content : '');
                                setEditFiles([]);
                              }}>
                                <i className="fas fa-edit"></i>
                              </button>
                              <button className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="삭제" onClick={() => {
                                const mainItem = group.items.find(i => !checkIsFile(i)) || group.items[0];
                                if (mainItem) handleDeleteComment(mainItem.id);
                              }}>
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* 텍스트 영역 */}
                      <div className="d-flex flex-column">
                        {editingCommentId === group.id ? (
                          <div className="px-3 py-2 d-flex flex-column gap-2">
                            <textarea className="form-control fs-8" value={editContent} onChange={e => setEditContent(e.target.value)} rows="2" placeholder="내용을 입력하세요..." />
                            <div className="d-flex align-items-center gap-2">
                              <div className="d-flex bg-white border flex-grow-1 align-items-center file-container position-relative" style={{ height: '28px', minWidth: 0 }}>
                                <div className="form-control border-0 shadow-none h-100 flex-grow-1 rounded-0 fs-8 px-2 bg-white d-flex align-items-center" onClick={() => editFileInputRef.current?.click()} style={{ minWidth: 0 }}>
                                  <span className={editFiles.length > 0 ? "text-dark text-truncate" : "text-muted text-truncate"} style={{ cursor: editFiles.length > 1 ? 'pointer' : 'default' }} onMouseEnter={(e) => handleFileLayerEnter(e, 'edit')} onMouseLeave={handleFileLayerLeave}>
                                    {editFiles.length > 0 ? `${editFiles[0].name}${editFiles.length > 1 ? ` 외 ${editFiles.length - 1}개` : ''}` : "추가할 파일을 선택하세요..."}
                                  </span>
                                </div>
                                {editFiles.length > 0 && (
                                  <i className="fas fa-times text-muted px-2 border-start d-flex align-items-center justify-content-center h-100 flex-shrink-0" style={{ cursor: 'pointer', width: '32px' }} onClick={() => setEditFiles([])} title="전체 삭제"></i>
                                )}
                                <input type="file" className="d-none" ref={editFileInputRef} multiple onChange={(e) => setEditFiles(Array.from(e.target.files))} />
                                <button className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center flex-shrink-0" title="파일 첨부" onClick={() => editFileInputRef.current?.click()}><i className="fas fa-upload fs-8"></i></button>
                              </div>
                              <button className="btn btn-sm text-white rounded-1 px-2 fw-bold fs-8 shadow-sm flex-shrink-0" style={{ backgroundColor: '#4c6ef5', height: '28px' }} onClick={async () => {
                                if (handleEditCommentSubmit) {
                                  const textItem = group.items.find(i => !checkIsFile(i));
                                  const success = await handleEditCommentSubmit(textItem ? textItem.id : null, editContent, editFiles, group.created_at);
                                  if (success) { setEditingCommentId(null); setEditFiles([]); }
                                }
                              }}>저장</button>
                              <button className="btn btn-sm text-white rounded-1 px-2 fw-bold fs-8 shadow-sm flex-shrink-0" style={{ backgroundColor: '#868e96', height: '28px' }} onClick={() => { setEditingCommentId(null); setEditFiles([]); }}>취소</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {group.items.filter(item => !checkIsFile(item)).map(item => (
                              <React.Fragment key={`text-wrap-${item.id}`}>
                                <div className="text-dark lh-base ps-3 pe-2 position-relative d-flex align-items-center action-container" style={{ paddingTop: '6px', paddingBottom: '6px', fontSize: '12px' }}>
                                  <div className="custom-scrollbar flex-grow-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '72px', overflowY: 'auto' }}>{item.content}</div>

                                </div>

                                {/* 텍스트 답글 렌더링 영역 (원문 텍스트 바로 아래로 복귀) */}
                                {(() => {
                                  const hasTextReplies = comments.some(c => c.parent_id === item.id && !checkIsFile(c));
                                  const isReplying = textReplyTargetId === item.id;
                                  
                                  if (!hasTextReplies && !isReplying) return null;

                                  return (
                                    <div className="py-2 pe-3" style={{ paddingLeft: '32px', borderTop: '1px solid rgba(0,0,0,0.06)', backgroundColor: '#fdfdfd' }}>
                                      {hasTextReplies && renderTextReplies(item.id)}
                                      
                                      {isReplying && (
                                        <div className="mt-2 pt-2" style={{ borderTop: '1px dashed rgba(0,0,0,0.08)' }}>
                                          <div className="d-flex gap-2 align-items-stretch">
                                            <textarea
                                              className="form-control form-control-sm shadow-none"
                                              rows="2"
                                              placeholder={`@${group.user?.name || '작성자'}님에게 텍스트 답글...`}
                                              value={replyContent}
                                              onChange={(e) => setReplyContent(e.target.value)}
                                              style={{ resize: 'none', fontSize: '12px', border: '1px solid #ced4da', flexGrow: 1 }}
                                              autoFocus
                                            ></textarea>
                                            <div className="d-flex flex-column gap-1 justify-content-center flex-shrink-0">
                                              <button className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-grow-1" style={{ backgroundColor: '#4c6ef5', fontSize: '10px', lineHeight: '1' }} onClick={() => {
                                                if (handleReplySubmit) {
                                                  handleReplySubmit(item.id, replyContent, [], group.user?.name || null);
                                                }
                                                setTextReplyTargetId(null);
                                                setReplyContent('');
                                              }}>저장</button>
                                              <button className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm flex-grow-1" style={{ backgroundColor: '#868e96', fontSize: '10px', lineHeight: '1' }} onClick={() => { setTextReplyTargetId(null); setReplyContent(''); }}>취소</button>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* 첨부파일 답글 렌더링 영역 */}
                                {replyTargetId === item.id && (
                                  <div className="bg-light border-top border-secondary border-opacity-10 d-flex flex-column py-1">
                                    {/* 첨부파일 전용 답글 (기존 구조 보존) */}
                                    <div className="mt-1 mx-3 mb-1 d-flex align-items-center gap-2 animation-fade-in">
                                      <i className="fas fa-level-up-alt fa-rotate-90 text-secondary ms-1 fs-5"></i>
                                      <div className="d-flex bg-white border flex-grow-1 align-items-center file-container position-relative" style={{ height: '32px', minWidth: 0 }}>
                                        <div className="form-control border-0 shadow-none h-100 flex-grow-1 rounded-0 fs-8 px-2 bg-white d-flex align-items-center" onClick={() => replyFileInputRef.current?.click()} style={{ minWidth: 0 }}>
                                          <span className={replyFiles.length > 0 ? "text-dark text-truncate" : "text-muted text-truncate"} style={{ cursor: replyFiles.length > 1 ? 'pointer' : 'default' }} onMouseEnter={(e) => handleFileLayerEnter(e, 'reply')} onMouseLeave={handleFileLayerLeave}>
                                            {replyFiles.length > 0 ? `${replyFiles[0].name}${replyFiles.length > 1 ? ` 외 ${replyFiles.length - 1}개` : ''}` : "첨부할 파일을 선택하세요..."}
                                          </span>
                                        </div>
                                        {replyFiles.length > 0 && (
                                          <i className="fas fa-times text-muted px-2 border-start d-flex align-items-center justify-content-center h-100 flex-shrink-0" style={{ cursor: 'pointer', width: '32px' }} onClick={() => setReplyFiles([])} title="전체 삭제"></i>
                                        )}
                                        <input type="file" className="d-none" ref={replyFileInputRef} multiple onChange={(e) => setReplyFiles(Array.from(e.target.files))} />
                                        <button className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center flex-shrink-0" title="파일 첨부" onClick={() => replyFileInputRef.current?.click()}><i className="fas fa-upload"></i></button>
                                      </div>
                                      <button className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm" style={{ backgroundColor: '#4c6ef5', height: '32px' }} onClick={() => { handleReplySubmit(item.id, replyContent, replyFiles, group.user?.name || null); setReplyFiles([]); setReplyContent(''); setReplyTargetId(null); }}>저장</button>
                                      <button className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm" style={{ backgroundColor: '#868e96', height: '32px' }} onClick={() => { setReplyTargetId(null); setReplyFiles([]); setReplyContent(''); }}>취소</button>
                                    </div>
                                  </div>
                                )}
                              </React.Fragment>
                            ))}

                            {addingFileCommentId === group.id && (
                              <div className="px-3 py-2 d-flex align-items-center gap-2 bg-light border-top border-bottom animation-fade-in">
                                <div className="d-flex bg-white border flex-grow-1 align-items-center file-container position-relative" style={{ height: '28px', minWidth: 0 }}>
                                  <div className="form-control border-0 shadow-none h-100 flex-grow-1 rounded-0 fs-8 px-2 bg-white d-flex align-items-center" onClick={() => additionalFileInputRef.current?.click()} style={{ minWidth: 0 }}>
                                    <span className={additionalFiles.length > 0 ? "text-dark text-truncate" : "text-muted text-truncate"} style={{ cursor: additionalFiles.length > 1 ? 'pointer' : 'default' }} onMouseEnter={(e) => handleFileLayerEnter(e, 'additional')} onMouseLeave={handleFileLayerLeave}>
                                      {additionalFiles.length > 0 ? `${additionalFiles[0].name}${additionalFiles.length > 1 ? ` 외 ${additionalFiles.length - 1}개` : ''}` : "추가할 파일을 선택하세요..."}
                                    </span>
                                  </div>
                                  {additionalFiles.length > 0 && (
                                    <i className="fas fa-times text-muted px-2 border-start d-flex align-items-center justify-content-center h-100 flex-shrink-0" style={{ cursor: 'pointer', width: '32px' }} onClick={() => setAdditionalFiles([])} title="전체 삭제"></i>
                                  )}
                                  <input type="file" className="d-none" ref={additionalFileInputRef} multiple onChange={(e) => setAdditionalFiles(Array.from(e.target.files))} />
                                  <button className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center flex-shrink-0" title="파일 선택" onClick={() => additionalFileInputRef.current?.click()}><i className="fas fa-upload fs-8"></i></button>
                                </div>
                                <button className="btn btn-sm text-white rounded-1 px-2 fw-bold fs-8 shadow-sm flex-shrink-0" style={{ backgroundColor: '#4c6ef5', height: '28px' }} onClick={async () => {
                                  if (handleEditCommentSubmit && additionalFiles.length > 0) {
                                    const success = await handleEditCommentSubmit(null, '', additionalFiles, group.created_at);
                                    if (success) { setAddingFileCommentId(null); setAdditionalFiles([]); }
                                  }
                                }}>저장</button>
                                <button className="btn btn-sm text-white rounded-1 px-2 fw-bold fs-8 shadow-sm flex-shrink-0" style={{ backgroundColor: '#868e96', height: '28px' }} onClick={() => { setAddingFileCommentId(null); setAdditionalFiles([]); }}>취소</button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* 첨부파일 영역 */}
                    <div className="d-flex flex-column">
                      {(() => {
                        const fileItems = group.items.filter(item => checkIsFile(item));
                        const originalFiles = fileItems.filter(item => !item.file?.created_at || new Date(item.file.created_at).getTime() - new Date(group.created_at).getTime() <= 60000);
                        const addedFiles = fileItems.filter(item => item.file?.created_at && new Date(item.file.created_at).getTime() - new Date(group.created_at).getTime() > 60000);
                        addedFiles.sort((a, b) => new Date(b.file.created_at) - new Date(a.file.created_at));
                        const sortedFileItems = [...originalFiles, ...addedFiles];

                        return sortedFileItems.map((item, fIdx) => {
                          const fileAuthorId = item.user_id || item.user?.id || item.user?.userid;
                          const rootAuthorId = group.user?.id || group.user?.userid;
                          const isSameAuthor = rootAuthorId && String(fileAuthorId) === String(rootAuthorId);
                          const fileName = item.user?.name || 'Unknown';
                          const filePos = item.user?.org?.position || item.user?.position ? ` ${item.user?.org?.position || item.user?.position}` : '';
                          const fileDept = item.user?.org?.dept_name || item.user?.dept_name ? ` [${item.user?.org?.dept_name || item.user?.dept_name}]` : '';
                          const fileFullName = `${fileName}${filePos}${fileDept}`;

                          const isAdded = item.file?.created_at && new Date(item.file.created_at).getTime() - new Date(group.created_at).getTime() > 60000;

                          return (
                            <React.Fragment key={`file-wrap-${item.id}`}>
                              <div className={`d-flex align-items-center action-container px-2 py-1 ${isAdded ? 'bg-light border-top border-white' : `bg-primary bg-opacity-10 ${fIdx === 0 ? 'border-top border-primary border-opacity-10' : 'border-top border-white border-opacity-50'}`}`}>
                                {isAdded && (
                                  <i className="fas fa-level-up-alt fa-rotate-90 text-secondary ms-2 me-3 fs-6 opacity-75"></i>
                                )}
                                <div className={`d-flex align-items-center justify-content-center ${isAdded ? 'bg-transparent text-primary' : 'bg-white rounded-circle shadow-sm'} me-2 flex-shrink-0`} style={{ width: '28px', height: '28px' }}>
                                  <i className={`${getFileIconClass(stripUniqueKey(item.file?.originalName || item.original_file_name || item.content))} ${isAdded ? 'fs-5' : 'fs-6'}`}></i>
                                </div>
                                <span
                                  className={`fs-7 fw-bold me-2 text-truncate flex-grow-0 ${isAdded ? 'text-dark' : 'text-primary'}`}
                                  style={{ maxWidth: 'calc(min(350px, 20vw))', cursor: 'pointer' }}
                                  onClick={() => handleDownloadFile(item.file)}
                                  onMouseEnter={(e) => {
                                    if (e.target.offsetWidth < e.target.scrollWidth) {
                                      e.target.title = stripUniqueKey(item.content);
                                    } else {
                                      e.target.title = '';
                                    }
                                  }}
                                >
                                  {stripUniqueKey(item.file?.originalName || item.original_file_name || item.content)}
                                </span>
                                <span className={`fs-8 opacity-75 flex-shrink-0 ${isAdded ? 'text-secondary' : 'text-primary'}`}>[{Math.round((item.file?.file_size || item.file_size || 1024) / 1024)}kb]</span>
                                
                                {isAdded && (
                                  <span className="fs-8 text-secondary ms-3 opacity-75">{formatDateTime(item.file?.created_at || item.created_at)}</span>
                                )}

                                <div className="d-flex align-items-center ms-auto flex-shrink-0">
                                  {!isAdded && item.file?.created_at && new Date(item.file.created_at).getTime() - new Date(group.created_at).getTime() > 60000 && (
                                    <span className="fs-8 text-secondary opacity-75 me-2 flex-shrink-0" style={{ fontSize: '11px' }}>
                                      {isSameAuthor ? `(추가됨: ${formatDateTime(item.file.created_at).slice(0, 16)})` : `(추가: ${fileFullName} / ${formatDateTime(item.file.created_at).slice(0, 16)})`}
                                    </span>
                                  )}

                                  <div className="d-flex gap-1 align-items-center opacity-0 action-buttons transition-opacity">
                                    <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="다운로드" onClick={() => handleDownloadFile(item.file)}>
                                      <i className="fas fa-download"></i>
                                    </button>
                                    {!isPureWatcher && !isAdded && (
                                      <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="답글(파일첨부)" onClick={() => setReplyTargetId(item.id)}>
                                        <i className="fas fa-plus"></i>
                                      </button>
                                    )}
                                    {!isPureWatcher && (isProjectMaster || (currentUser && currentUser.name === item.user?.name)) && (
                                      <button className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="삭제" onClick={() => handleDeleteComment(item.id)}>
                                        <i className="fas fa-trash-alt"></i>
                                      </button>
                                    )}
                                    {!isPureWatcher && !isAdded && (isProjectMaster || (currentUser && currentUser.name === item.user?.name)) && (
                                      folders && folders.length === 0 ? (
                                        <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded opacity-50" style={{ background: 'transparent', cursor: 'not-allowed' }} title="생성된 폴더가 존재하지 않습니다." disabled>
                                          <i className="far fa-folder"></i>
                                        </button>
                                      ) : (
                                        <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="폴더이동" onClick={() => openFolderModal(item.id)}>
                                          <i className="far fa-folder"></i>
                                        </button>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* 개별 파일 바로 밑에 붙는 답글 및 입력 폼 */}
                              {(replyTargetId === item.id || comments.some(c => c.parent_id === item.id)) && (
                                <div className="bg-light border-top border-secondary border-opacity-10 d-flex flex-column py-1">
                                  {replyTargetId === item.id && (
                                    <div className="mt-1 mx-3 mb-1 d-flex align-items-center gap-2 animation-fade-in">
                                      <i className="fas fa-level-up-alt fa-rotate-90 text-secondary ms-1 fs-5"></i>
                                      <div className="d-flex bg-white border flex-grow-1 align-items-center file-container position-relative" style={{ height: '32px', minWidth: 0 }}>
                                        <div className="form-control border-0 shadow-none h-100 flex-grow-1 rounded-0 fs-8 px-2 bg-white d-flex align-items-center" onClick={() => replyFileInputRef.current?.click()} style={{ minWidth: 0 }}>
                                          <span className={replyFiles.length > 0 ? "text-dark text-truncate" : "text-muted text-truncate"} style={{ cursor: replyFiles.length > 1 ? 'pointer' : 'default' }} onMouseEnter={(e) => handleFileLayerEnter(e, 'reply')} onMouseLeave={handleFileLayerLeave}>
                                            {replyFiles.length > 0 ? `${replyFiles[0].name}${replyFiles.length > 1 ? ` 외 ${replyFiles.length - 1}개` : ''}` : "첨부할 파일을 선택하세요..."}
                                          </span>
                                        </div>
                                        {replyFiles.length > 0 && (
                                          <i className="fas fa-times text-muted px-2 border-start d-flex align-items-center justify-content-center h-100 flex-shrink-0" style={{ cursor: 'pointer', width: '32px' }} onClick={() => setReplyFiles([])} title="전체 삭제"></i>
                                        )}
                                        <input type="file" className="d-none" ref={replyFileInputRef} multiple onChange={(e) => setReplyFiles(Array.from(e.target.files))} />
                                        <button className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center flex-shrink-0" title="파일 첨부" onClick={() => replyFileInputRef.current?.click()}><i className="fas fa-upload"></i></button>
                                      </div>
                                      <button className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm" style={{ backgroundColor: '#4c6ef5', height: '32px' }} onClick={() => { handleReplySubmit(item.id, replyContent, replyFiles, group.user?.name || null); setReplyFiles([]); setReplyContent(''); setReplyTargetId(null); }}>저장</button>
                                      <button className="btn btn-sm text-white rounded-1 px-3 fw-bold shadow-sm" style={{ backgroundColor: '#868e96', height: '32px' }} onClick={() => { setReplyTargetId(null); setReplyFiles([]); setReplyContent(''); }}>취소</button>
                                    </div>
                                  )}
                                  {comments.some(c => c.parent_id === item.id) && (
                                    renderReplies(item.id, group.user?.id || group.user?.userid, 1)
                                  )}
                                </div>
                              )}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {renderPortalPopup()}
    </div>
  );
};

export default CommentsTab;
